import { useCallback, useEffect, useMemo, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import {
  BookOpen,
  Boxes,
  Calendar,
  ClipboardList,
  Download,
  FileText,
  GitBranch,
  Globe,
  PlayCircle,
  RefreshCw,
  Shield,
  Sparkles,
  Users,
  Wand2,
} from 'lucide-react';
import { toast } from 'sonner';
import { resetWelcomeTour } from '@/components/WelcomeTour';
import { manualAPI } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EMPTY_INFO = {
  product_name: 'CG Business ERP',
  version: '3.5.0',
  semantic_version: '3.5.0',
  date_version: '2026.03.18.1',
  edition: 'Go-Live Operacional',
  modules: 24,
  access_levels: 12,
  last_updated: '--/--/----',
  go_live_status: 'Sistema em operação diária com histórico activo de versões',
  release_title: 'Actualização operacional do ERP',
  release_summary: 'Sem resumo publicado ainda.',
  manual_focus: 'Manual vivo por perfil com histórico de versões, módulos e rotinas-chave.',
  release_notes: [],
  release_history: [],
  profile_guides: [],
  module_overview: [],
};

const buildEditorState = (info) => ({
  release_title: info.release_title || '',
  release_summary: info.release_summary || '',
  edition: info.edition || '',
  go_live_status: info.go_live_status || '',
  manual_focus: info.manual_focus || '',
  release_notes_text: (info.release_notes || []).map((note) => note.summary || note.title).join('\n'),
});

const buildPayloadFromEditor = (editorState) => ({
  release_title: editorState.release_title.trim(),
  release_summary: editorState.release_summary.trim(),
  edition: editorState.edition.trim(),
  go_live_status: editorState.go_live_status.trim(),
  manual_focus: editorState.manual_focus.trim(),
  release_notes: editorState.release_notes_text
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean),
});

const MiniStat = ({ icon: Icon, label, value, testId }) => (
  <div className="rounded-2xl border border-slate-200 bg-white/90 px-3 py-3" data-testid={testId}>
    <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-slate-500">
      <Icon className="h-3.5 w-3.5 text-[#2B4DAA]" />
      <span>{label}</span>
    </div>
    <div className="mt-1 text-sm font-semibold text-slate-900">{value}</div>
  </div>
);

const downloadBlobFile = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

const getFilenameFromDisposition = (contentDisposition, fallback) => {
  if (!contentDisposition) return fallback;
  const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) return decodeURIComponent(utf8Match[1]);
  const filenameMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
  return filenameMatch?.[1] || fallback;
};

const ProfileWorkflowCard = ({ guide, guideIndex }) => (
  <div className="rounded-[24px] border border-slate-200 bg-white/90 p-3" data-testid={`about-profile-workflow-${guideIndex}`}>
    <div className="flex flex-col gap-2 border-b border-slate-200 pb-3 sm:flex-row sm:items-start sm:justify-between">
      <div>
        <div className="text-sm font-semibold text-slate-950">{guide.title}</div>
        <div className="mt-1 text-xs text-slate-500">Fluxos screen-by-screen para {guide.title.toLowerCase()}.</div>
      </div>
      <Badge variant="outline" className="w-fit rounded-full">{guide.screen_flows?.length || 0} fluxos</Badge>
    </div>

    {(guide.screen_flows || []).map((flow, flowIndex) => (
      <div key={`${guide.role_key}-flow-${flowIndex}`} className="mt-3 rounded-2xl border border-slate-200 bg-slate-50 px-3 py-3" data-testid={`about-profile-workflow-flow-${guideIndex}-${flowIndex}`}>
        <div className="flex flex-col gap-2 xl:flex-row xl:items-start xl:justify-between">
          <div>
            <div className="text-sm font-semibold text-slate-950">{flow.title}</div>
            <p className="mt-1 text-xs text-slate-600">{flow.goal}</p>
          </div>
          <Badge variant="outline" className="w-fit rounded-full text-[11px]">{flow.steps?.length || 0} passos</Badge>
        </div>

        <div className="mt-3 grid gap-2 xl:grid-cols-2">
          {(flow.steps || []).map((step, stepIndex) => (
            <div key={`${guide.role_key}-flow-${flowIndex}-step-${stepIndex}`} className="rounded-xl border border-slate-200 bg-white px-3 py-3" data-testid={`about-profile-workflow-step-${guideIndex}-${flowIndex}-${stepIndex}`}>
              <div className="flex items-center justify-between gap-3">
                <span className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Passo {stepIndex + 1}</span>
                <Badge variant="outline" className="rounded-full text-[11px]">{step.screen}</Badge>
              </div>
              <div className="mt-2 text-xs text-slate-500">{step.route}</div>
              <p className="mt-2 text-sm text-slate-700">{step.action}</p>
              <div className="mt-2 rounded-xl border border-emerald-200 bg-emerald-50 px-2.5 py-2 text-xs text-emerald-700">
                <strong>Check:</strong> {step.check}
              </div>
            </div>
          ))}
        </div>
      </div>
    ))}
  </div>
);

export default function AboutPage() {
  const { hasPermission } = useAuth();
  const [manualInfo, setManualInfo] = useState(EMPTY_INFO);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [downloadingProfileRole, setDownloadingProfileRole] = useState('');
  const [saving, setSaving] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorMode, setEditorMode] = useState('update');
  const [editorState, setEditorState] = useState(buildEditorState(EMPTY_INFO));
  const [selectedProfileRole, setSelectedProfileRole] = useState('');

  const canManageRelease = hasPermission('settings.edit');
  const releaseNotes = manualInfo.release_notes || [];
  const releaseHistory = manualInfo.release_history || [];
  const moduleOverview = manualInfo.module_overview || [];
  const profileGuides = manualInfo.profile_guides || [];
  const selectedProfileGuide = useMemo(() => profileGuides.find((guide) => guide.role_key === selectedProfileRole) || profileGuides[0] || null, [profileGuides, selectedProfileRole]);

  const versionLabel = useMemo(() => {
    const semanticVersion = manualInfo.semantic_version || manualInfo.version;
    if (!semanticVersion) return '--';
    return `v${semanticVersion}`;
  }, [manualInfo.semantic_version, manualInfo.version]);

  const handleDownloadManual = () => {
    setDownloading(true);
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = `${API_URL}/api/manual/download`;
    document.body.appendChild(iframe);

    setTimeout(() => {
      if (iframe.parentNode) {
        document.body.removeChild(iframe);
      }
      setDownloading(false);
      toast.success('Download do manual iniciado com sucesso.');
    }, 1800);
  };

  const handleDownloadDoc = (path, successLabel) => {
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = `${API_URL}${path}`;
    document.body.appendChild(iframe);
    setTimeout(() => {
      if (iframe.parentNode) {
        document.body.removeChild(iframe);
      }
      toast.success(successLabel);
    }, 1500);
  };

  const handleDownloadProfileSop = async (roleKey = selectedProfileGuide?.role_key, title = selectedProfileGuide?.title) => {
    if (!roleKey) {
      toast.error('Seleccione um perfil antes de exportar o SOP.');
      return;
    }

    setDownloadingProfileRole(roleKey);
    try {
      const response = await manualAPI.downloadProfileSop(roleKey);
      const fallbackFilename = `CG_Business_ERP_SOP_${roleKey}.pdf`;
      const filename = getFilenameFromDisposition(response?.headers?.['content-disposition'], fallbackFilename);
      downloadBlobFile(response.data, filename);
      toast.success(`SOP do perfil ${title || roleKey} exportado com sucesso.`);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Não foi possível exportar o SOP deste perfil.');
    } finally {
      setDownloadingProfileRole('');
    }
  };

  const handleRestartTour = async () => {
    try {
      await resetWelcomeTour();
      toast.success('Tour reiniciado. O percurso guiado vai abrir no Dashboard.');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Não foi possível reiniciar o tour.');
    }
  };

  const loadManualInfo = useCallback(async () => {
    setLoading(true);
    try {
      const response = await manualAPI.getInfo();
      const data = response?.data || EMPTY_INFO;
      setManualInfo({ ...EMPTY_INFO, ...data });
      setEditorState(buildEditorState({ ...EMPTY_INFO, ...data }));
    } catch {
      toast.error('Não foi possível carregar o About actualizado.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadManualInfo();
  }, [loadManualInfo]);

  useEffect(() => {
    if (!profileGuides.length) {
      setSelectedProfileRole('');
      return;
    }

    if (!profileGuides.some((guide) => guide.role_key === selectedProfileRole)) {
      setSelectedProfileRole(profileGuides[0].role_key);
    }
  }, [profileGuides, selectedProfileRole]);

  const openEditor = (mode) => {
    setEditorMode(mode);
    setEditorState(buildEditorState(manualInfo));
    setEditorOpen(true);
  };

  const handleSaveEditor = async () => {
    const payload = buildPayloadFromEditor(editorState);
    setSaving(true);
    try {
      if (editorMode === 'publish') {
        await manualAPI.publishRelease(payload);
        toast.success('Nova versão publicada no About.');
      } else {
        await manualAPI.updateCurrentRelease(payload);
        toast.success('Release actualizada no About.');
      }
      setEditorOpen(false);
      await loadManualInfo();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Não foi possível guardar a actualização.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-[radial-gradient(circle_at_top_left,_rgba(246,147,31,0.12),_transparent_28%),radial-gradient(circle_at_top_right,_rgba(43,77,170,0.12),_transparent_24%),linear-gradient(180deg,#f8fafc_0%,#ffffff_48%,#eef2ff_100%)] px-4 py-4 sm:px-6"
      data-testid="about-page"
    >
      <div className="mx-auto max-w-[1600px] space-y-4">
        <Card className="overflow-hidden rounded-[28px] border border-slate-200 bg-white/90 shadow-[0_18px_60px_-30px_rgba(15,23,42,0.35)]" data-testid="about-hero-card">
          <CardContent className="p-4 sm:p-5">
            <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
              <div className="space-y-3">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge className="rounded-full bg-[#2B4DAA] px-3 py-1 text-white" data-testid="about-current-semantic-version-badge">{versionLabel}</Badge>
                  <Badge variant="outline" className="rounded-full border-[#F6931F] text-[#F6931F]" data-testid="about-current-date-version-badge">{manualInfo.date_version || '--'}</Badge>
                  <Badge variant="outline" className="rounded-full border-emerald-500 text-emerald-600" data-testid="about-current-status-badge">{manualInfo.go_live_status}</Badge>
                </div>
                <div>
                  <h1 className="text-4xl font-semibold tracking-tight text-slate-950 sm:text-5xl" data-testid="about-page-title">About · Versões · Manual</h1>
                  <p className="mt-2 max-w-4xl text-sm text-slate-600 sm:text-base" data-testid="about-release-summary-text">
                    {manualInfo.release_summary}
                  </p>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-3 text-sm text-slate-700" data-testid="about-manual-focus-card">
                  <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-slate-500">
                    <Wand2 className="h-3.5 w-3.5 text-[#F6931F]" />
                    <span>Manual vivo</span>
                  </div>
                  <p className="mt-2">{manualInfo.manual_focus}</p>
                </div>
              </div>

              <div className="grid w-full gap-3 sm:grid-cols-2 xl:max-w-[520px]" data-testid="about-stats-grid">
                <MiniStat icon={GitBranch} label="Versão actual" value={`${versionLabel} • ${manualInfo.date_version || '--'}`} testId="about-current-version-stat" />
                <MiniStat icon={Calendar} label="Última actualização" value={manualInfo.last_updated || '--'} testId="about-last-updated-stat" />
                <MiniStat icon={Boxes} label="Módulos activos" value={manualInfo.modules || 0} testId="about-modules-stat" />
                <MiniStat icon={Users} label="Perfis documentados" value={manualInfo.access_levels || 0} testId="about-profiles-stat" />
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid gap-4 xl:grid-cols-[1.45fr_1fr]">
          <Card className="rounded-[26px] border border-slate-200 bg-white/90" data-testid="about-actions-card">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <BookOpen className="h-4 w-4 text-[#2B4DAA]" />
                Acções rápidas de documentação
              </CardTitle>
              <CardDescription>Manual, dossier técnico, relatório final e controlo da versão actual.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                <Button onClick={handleDownloadManual} disabled={downloading} className="justify-start gap-2 rounded-2xl bg-[#F6931F] text-white hover:bg-[#e8850f]" data-testid="download-manual-btn">
                  {downloading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                  Descarregar Manual
                </Button>
                <Button variant="outline" onClick={() => handleDownloadDoc('/api/manual/dossier-tecnico', 'Download do dossier técnico iniciado.')} className="justify-start gap-2 rounded-2xl" data-testid="download-dossier-btn">
                  <BookOpen className="h-4 w-4" />
                  Dossier Técnico
                </Button>
                <Button variant="outline" onClick={() => handleDownloadDoc('/api/manual/go-live-report', 'Download do relatório go-live iniciado.')} className="justify-start gap-2 rounded-2xl" data-testid="download-go-live-report-btn">
                  <FileText className="h-4 w-4" />
                  Relatório Go-Live
                </Button>
                <Button variant="outline" onClick={handleRestartTour} className="justify-start gap-2 rounded-2xl border-[#2B4DAA] text-[#2B4DAA]" data-testid="restart-tour-btn">
                  <PlayCircle className="h-4 w-4" />
                  Reiniciar Tour
                </Button>
                {canManageRelease && (
                  <Button variant="outline" onClick={() => openEditor('update')} className="justify-start gap-2 rounded-2xl border-emerald-600 text-emerald-700" data-testid="about-edit-current-release-button">
                    <Sparkles className="h-4 w-4" />
                    Actualizar release actual
                  </Button>
                )}
                {canManageRelease && (
                  <Button onClick={() => openEditor('publish')} className="justify-start gap-2 rounded-2xl bg-[#2B4DAA] text-white hover:bg-[#1f3d8f]" data-testid="about-publish-release-button">
                    <GitBranch className="h-4 w-4" />
                    Publicar nova versão
                  </Button>
                )}
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-3 text-xs text-slate-600" data-testid="about-release-title-box">
                <strong className="text-slate-900">Release actual:</strong> {manualInfo.release_title}
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-[26px] border border-slate-200 bg-white/90" data-testid="about-system-card">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <Shield className="h-4 w-4 text-[#F6931F]" />
                Sistema e propriedade
              </CardTitle>
              <CardDescription>Informação institucional da instalação actual.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3 text-sm text-slate-700">
              <div className="flex items-center justify-between rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2" data-testid="about-system-edition-row">
                <span>Edição</span>
                <span className="font-semibold text-slate-950" data-testid="about-system-edition-value">{manualInfo.edition}</span>
              </div>
              <div className="flex items-center justify-between rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2" data-testid="about-system-last-updated-row">
                <span>Última Actualização</span>
                <span className="font-semibold text-slate-950" data-testid="about-system-last-updated-value">{manualInfo.last_updated}</span>
              </div>
              <div className="flex items-center justify-between rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2" data-testid="about-system-company-row">
                <span>Empresa</span>
                <span className="font-semibold text-slate-950">{manualInfo.company || 'CG Business, Lda'}</span>
              </div>
              <div className="flex items-center justify-between rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2" data-testid="about-system-author-row">
                <span>Autor</span>
                <span className="font-semibold text-slate-950">{manualInfo.author || 'Charles Gabell'}</span>
              </div>
              <div className="flex items-center justify-between rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2" data-testid="about-system-country-row">
                <span>Local</span>
                <span className="inline-flex items-center gap-2 font-semibold text-slate-950"><Globe className="h-4 w-4 text-[#2B4DAA]" /> {manualInfo.location || 'Maputo, Moçambique'}</span>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid gap-4 xl:grid-cols-[0.95fr_1.05fr]">
          <Card className="rounded-[26px] border border-slate-200 bg-white/90" data-testid="about-current-release-notes-card">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <ClipboardList className="h-4 w-4 text-[#2B4DAA]" />
                O que esta versão inclui
              </CardTitle>
              <CardDescription>Notas activas desta versão do sistema.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              {loading ? (
                <div className="rounded-2xl border border-dashed border-slate-300 px-3 py-4 text-sm text-slate-500" data-testid="about-loading-state">A carregar About actualizado...</div>
              ) : releaseNotes.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-slate-300 px-3 py-4 text-sm text-slate-500" data-testid="about-empty-release-notes">Sem notas publicadas para a versão actual.</div>
              ) : (
                releaseNotes.map((note, index) => (
                  <div key={note.id || `${note.title}-${index}`} className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-3" data-testid={`about-release-note-${index}`}>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-semibold text-slate-950">{note.title || note.summary}</span>
                      {note.date_label ? <Badge variant="outline" className="rounded-full">{note.date_label}</Badge> : null}
                    </div>
                    <p className="mt-1 text-sm text-slate-600">{note.summary}</p>
                  </div>
                ))
              )}
            </CardContent>
          </Card>

          <Card className="rounded-[26px] border border-slate-200 bg-white/90" data-testid="about-modules-overview-card">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <Boxes className="h-4 w-4 text-[#F6931F]" />
                Mapa rápido dos módulos
              </CardTitle>
              <CardDescription>Resumo compacto para onboarding e revisão operacional.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
                {moduleOverview.map((module, index) => (
                  <div key={module.key || `${module.name}-${index}`} className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-3" data-testid={`about-module-card-${index}`}>
                    <div className="text-sm font-semibold text-slate-950">{module.name}</div>
                    <div className="mt-1 text-xs text-slate-500">{module.route}</div>
                    <p className="mt-2 text-xs text-slate-600">{module.summary}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        <Card className="rounded-[26px] border border-slate-200 bg-white/90" data-testid="about-profile-sop-export-card">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <FileText className="h-4 w-4 text-[#2B4DAA]" />
              Export SOP por perfil
            </CardTitle>
            <CardDescription>Seleccione um perfil já documentado no About e exporte um PDF específico, pronto para consulta e impressão.</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4 xl:grid-cols-[0.9fr_1.1fr]">
            <div className="space-y-3">
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-slate-600">Perfil documentado</label>
                <Select value={selectedProfileGuide?.role_key || ''} onValueChange={setSelectedProfileRole}>
                  <SelectTrigger className="rounded-2xl bg-white" data-testid="about-profile-sop-select">
                    <SelectValue placeholder="Seleccionar perfil" />
                  </SelectTrigger>
                  <SelectContent>
                    {profileGuides.map((guide) => (
                      <SelectItem key={guide.role_key} value={guide.role_key}>
                        {guide.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Button
                onClick={() => handleDownloadProfileSop()}
                disabled={!selectedProfileGuide || downloadingProfileRole === selectedProfileGuide?.role_key}
                className="justify-start gap-2 rounded-2xl bg-[#2B4DAA] text-white hover:bg-[#1f3d8f]"
                data-testid="about-profile-sop-download-button"
              >
                {downloadingProfileRole === selectedProfileGuide?.role_key ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                Exportar SOP em PDF
              </Button>
            </div>

            <div className="rounded-[24px] border border-slate-200 bg-slate-50 px-4 py-4" data-testid="about-profile-sop-summary-card">
              {selectedProfileGuide ? (
                <div className="space-y-3">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="text-sm font-semibold text-slate-950">{selectedProfileGuide.title}</div>
                      <div className="mt-1 text-xs text-slate-500">Entrada sugerida: {selectedProfileGuide.landing_page || '/dashboard'}</div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Badge variant="outline" className="rounded-full">{selectedProfileGuide.primary_modules?.length || 0} módulos</Badge>
                      <Badge variant="outline" className="rounded-full">{selectedProfileGuide.screen_flows?.length || 0} fluxos</Badge>
                    </div>
                  </div>
                  <p className="text-sm text-slate-600">{selectedProfileGuide.summary}</p>
                  <div className="flex flex-wrap gap-2">
                    {(selectedProfileGuide.primary_modules || []).map((module) => (
                      <Badge key={`${selectedProfileGuide.role_key}-${module}-sop`} variant="outline" className="rounded-full text-[11px]">{module}</Badge>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-sm text-slate-500">Sem perfis documentados disponíveis para exportar.</div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-[26px] border border-slate-200 bg-white/90" data-testid="about-profile-guides-card">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Users className="h-4 w-4 text-[#2B4DAA]" />
              Guias rápidos por perfil
            </CardTitle>
            <CardDescription>O tour e o manual passam a seguir este mapa funcional por função de utilizador.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3 lg:grid-cols-2 2xl:grid-cols-3">
              {profileGuides.map((guide, index) => (
                <div key={guide.role_key || `${guide.title}-${index}`} className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-3" data-testid={`about-profile-guide-${index}`}>
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div className="text-sm font-semibold text-slate-950">{guide.title}</div>
                      <div className="text-xs text-slate-500">Entrada sugerida: {guide.landing_page || '/dashboard'}</div>
                    </div>
                    <Badge variant="outline" className="rounded-full">{guide.primary_modules?.length || 0} módulos</Badge>
                  </div>
                  <p className="mt-2 text-sm text-slate-600">{guide.summary}</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {(guide.primary_modules || []).map((module) => (
                      <Badge key={`${guide.role_key}-${module}`} variant="outline" className="rounded-full text-[11px]">{module}</Badge>
                    ))}
                  </div>
                  {(guide.tour_quick_path || []).length > 0 ? (
                    <div className="mt-3 rounded-xl border border-[#F6931F]/20 bg-[#fff7ed] px-2.5 py-2 text-xs text-slate-700" data-testid={`about-profile-tour-path-${index}`}>
                      <div className="mb-1 font-semibold text-[#b96405]">Roteiro rápido do tour</div>
                      <div className="space-y-1">
                        {(guide.tour_quick_path || []).map((item, itemIndex) => (
                          <div key={`${guide.role_key}-tour-${itemIndex}`}>• {item}</div>
                        ))}
                      </div>
                    </div>
                  ) : null}
                  <div className="mt-3 space-y-1.5 text-xs text-slate-600">
                    {(guide.daily_routine || []).slice(0, 3).map((step, stepIndex) => (
                      <div key={`${guide.role_key}-routine-${stepIndex}`} className="rounded-xl border border-slate-200 bg-white px-2.5 py-2" data-testid={`about-profile-routine-${index}-${stepIndex}`}>
                        {step}
                      </div>
                    ))}
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => handleDownloadProfileSop(guide.role_key, guide.title)}
                    disabled={downloadingProfileRole === guide.role_key}
                    className="mt-3 w-full justify-center gap-2 rounded-2xl"
                    data-testid={`about-profile-sop-card-button-${index}`}
                  >
                    {downloadingProfileRole === guide.role_key ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                    Exportar SOP deste perfil
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-[26px] border border-slate-200 bg-white/90" data-testid="about-screen-flows-card">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <ClipboardList className="h-4 w-4 text-[#F6931F]" />
              Fluxos operacionais detalhados por função
            </CardTitle>
            <CardDescription>Passo a passo por ecrã para onboarding, execução diária e auditoria interna.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3 2xl:grid-cols-2">
              {profileGuides.map((guide, index) => (
                <ProfileWorkflowCard key={guide.role_key || `${guide.title}-${index}`} guide={guide} guideIndex={index} />
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-[26px] border border-slate-200 bg-white/90" data-testid="about-release-history-card">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <GitBranch className="h-4 w-4 text-[#F6931F]" />
              Histórico de versões do sistema
            </CardTitle>
            <CardDescription>Cada versão publicada fica registada e visível no próprio About.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {releaseHistory.map((release, index) => (
              <div key={release.id || `${release.version_label}-${index}`} className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-3" data-testid={`about-release-history-row-${index}`}>
                <div className="flex flex-col gap-2 xl:flex-row xl:items-start xl:justify-between">
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-semibold text-slate-950">{release.version_label || `v${release.version || '--'}`}</span>
                      <Badge variant={release.is_current ? 'default' : 'outline'} className="rounded-full">{release.is_current ? 'Actual' : 'Histórico'}</Badge>
                      <Badge variant="outline" className="rounded-full">{release.source || 'auto'}</Badge>
                    </div>
                    <div className="mt-1 text-sm font-medium text-slate-800">{release.release_title || release.edition}</div>
                    <p className="mt-1 text-sm text-slate-600">{release.release_summary}</p>
                  </div>
                  <div className="text-xs text-slate-500">{release.last_updated || '--'}</div>
                </div>
                {(release.release_notes || []).length > 0 ? (
                  <div className="mt-3 grid gap-2 xl:grid-cols-3">
                    {(release.release_notes || []).slice(0, 3).map((note, noteIndex) => (
                      <div key={note.id || `${release.id}-note-${noteIndex}`} className="rounded-xl border border-slate-200 bg-white px-2.5 py-2 text-xs text-slate-600" data-testid={`about-release-history-note-${index}-${noteIndex}`}>
                        {note.summary || note.title}
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            ))}
          </CardContent>
        </Card>

        <div className="pb-2 text-center text-xs text-slate-500" data-testid="about-footer-text">
          {manualInfo.product_name || 'CG Business ERP'} • {versionLabel} • {manualInfo.edition} • {manualInfo.last_updated}
        </div>
      </div>

      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="max-w-3xl rounded-[28px] border border-slate-200 p-0" data-testid="about-release-editor-dialog">
          <div className="max-h-[88vh] overflow-y-auto p-5 sm:p-6">
            <DialogHeader>
              <DialogTitle className="text-lg font-semibold text-slate-950">
                {editorMode === 'publish' ? 'Publicar nova versão' : 'Actualizar release actual'}
              </DialogTitle>
              <DialogDescription>
                {editorMode === 'publish'
                  ? 'Cria uma nova versão no histórico do About mantendo o registo completo das entregas.'
                  : 'Ajusta a release actual sem criar uma nova linha de versão.'}
              </DialogDescription>
            </DialogHeader>

            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-slate-600">Título da release</label>
                <Input value={editorState.release_title} onChange={(event) => setEditorState((current) => ({ ...current, release_title: event.target.value }))} data-testid="about-release-editor-title-input" />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-slate-600">Edição</label>
                <Input value={editorState.edition} onChange={(event) => setEditorState((current) => ({ ...current, edition: event.target.value }))} data-testid="about-release-editor-edition-input" />
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <label className="text-xs font-medium text-slate-600">Resumo executivo</label>
                <Textarea value={editorState.release_summary} onChange={(event) => setEditorState((current) => ({ ...current, release_summary: event.target.value }))} rows={3} data-testid="about-release-editor-summary-input" />
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <label className="text-xs font-medium text-slate-600">Estado / Última Actualização</label>
                <Input value={editorState.go_live_status} onChange={(event) => setEditorState((current) => ({ ...current, go_live_status: event.target.value }))} data-testid="about-release-editor-status-input" />
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <label className="text-xs font-medium text-slate-600">Foco do manual</label>
                <Textarea value={editorState.manual_focus} onChange={(event) => setEditorState((current) => ({ ...current, manual_focus: event.target.value }))} rows={3} data-testid="about-release-editor-focus-input" />
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <label className="text-xs font-medium text-slate-600">Notas da versão (uma por linha)</label>
                <Textarea value={editorState.release_notes_text} onChange={(event) => setEditorState((current) => ({ ...current, release_notes_text: event.target.value }))} rows={6} data-testid="about-release-editor-notes-input" />
              </div>
            </div>

            <DialogFooter className="mt-5 gap-2">
              <Button variant="outline" onClick={() => setEditorOpen(false)} data-testid="about-release-editor-cancel-button">Cancelar</Button>
              <Button onClick={handleSaveEditor} disabled={saving} className="bg-[#2B4DAA] text-white hover:bg-[#1f3d8f]" data-testid="about-release-editor-save-button">
                {saving ? <RefreshCw className="mr-2 h-4 w-4 animate-spin" /> : null}
                {editorMode === 'publish' ? 'Publicar versão' : 'Guardar actualização'}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}